/* Nesting runs in a Web Worker so the page never freezes. */

import type { BuildResult, FillResult, ProjectDoc, BuilderSettings, PricingConfig } from '@gsb/core';

type Req =
  | { kind: 'build' | 'renest'; doc: ProjectDoc; builder: BuilderSettings; pricing: PricingConfig }
  | { kind: 'fill'; doc: ProjectDoc; builder: BuilderSettings; sheetIndex: number; designId: string; widthMm: number; heightMm: number; limit?: number; rotate?: boolean };

let worker: Worker | null = null;
let seq = 0;
const waiting = new Map<number, { resolve: (v: any) => void; reject: (e: Error) => void }>();

function get(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('../workers/nest.worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (e: MessageEvent) => {
    const { id, ok, result, error } = e.data;
    const w = waiting.get(id);
    if (!w) return;
    waiting.delete(id);
    if (ok) w.resolve(result); else w.reject(new Error(error));
  };
  worker.onerror = e => {
    waiting.forEach(w => w.reject(new Error(e.message || 'The layout engine stopped.')));
    waiting.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function call<T>(r: Req): Promise<T> {
  const id = ++seq;
  return new Promise<T>((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    get().postMessage({ id, ...r });
  });
}

export const nest = {
  build: (doc: ProjectDoc, builder: BuilderSettings, pricing: PricingConfig) => call<BuildResult>({ kind: 'build', doc, builder, pricing }),
  renest: (doc: ProjectDoc, builder: BuilderSettings, pricing: PricingConfig) => call<BuildResult>({ kind: 'renest', doc, builder, pricing }),
  fill: (doc: ProjectDoc, builder: BuilderSettings, sheetIndex: number, designId: string, widthMm: number, heightMm: number, limit?: number, rotate = true) =>
    call<FillResult>({ kind: 'fill', doc, builder, sheetIndex, designId, widthMm, heightMm, limit, rotate })
};
